import React from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { AutoComplete } from 'primereact/autocomplete';
import acordLogo from 'images/acordlogo.png';
import CreateNewCarriers from '../CreateNewCarriers';
import AddNewSignature from '../AddNewSignature';
import { useSignatures } from '../../../../hooks/useSignatures';
import ProducerSection, { fetchPolicyAgentContact, fetchAgentContact } from '../ProducerSection';
import { useCertificateHolders } from '../../../certificate-holders/hooks/useCertificateHolders';
import { formatUsPhone } from 'utilities/formatPhone';
import InsurerSearchDropdown, { resolveCarrierIdByName, lookupCarrierIdSync } from '../InsurerSearchDropdown';
import { mapFields, getInsurers, getAllRemarkLines } from '../acordFieldEngine';
import type { AcordFieldItem } from '../acordFieldEngine';
import { PROPERTY_MAP, INLAND_MAP, CRIME_MAP, BOILER_MAP, OTHER24_MAP } from './acord24FieldMap';
import type { Acord24FormData } from '../../utils/acord24Payload';
import { fetchBuildingCoverages } from '../../api/masterCertificateApi';
import { useSelectedClient } from '../../../../hooks/useSelectedClient';
import { parseAcord24StoredCoverages } from '../../utils/parseAcord24StoredCoverages';
import type { StoredCoverage, StoredPolicy } from '../../utils/parseAcord24StoredCoverages';

interface Acord24FormProps {
  showAcord101?: boolean;
  isV2016?: boolean;
  selectedPolicies?: string[];
  insuredId?: string;
  selectedLocationLabel?: string;
  editCoverages?: StoredCoverage[];
  editPolicies?: StoredPolicy[];
  onLoadingChange?: (loading: boolean) => void;
}

export interface Acord24FormRef {
  getFormData(): Acord24FormData;
  applyBuildingCoverages(returnObjects: Array<{ fieldName: string; value: string }>): void;
}

const INSR_OPTIONS = ['A','B','C','D','E','F'].map(l => ({ label: l, value: l }));

const Acord24Form = React.forwardRef<Acord24FormRef, Acord24FormProps>(
function Acord24Form({ showAcord101 = false, isV2016 = false, selectedPolicies = [], insuredId = '', selectedLocationLabel = '', editCoverages, editPolicies, onLoadingChange }, ref) {
  const { client } = useSelectedClient();
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);
  const [showAddSignature, setShowAddSignature] = React.useState(false);
  const [producerAddress, setProducerAddress] = React.useState('');
  const [producerCity, setProducerCity] = React.useState('');
  const [producerState, setProducerState] = React.useState('');
  const [producerZip, setProducerZip] = React.useState('');
  const [producerStreet, setProducerStreet] = React.useState('');
  const [contactName, setContactName] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [contactFax, setContactFax] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [producerCustomerId, setProducerCustomerId] = React.useState('');
  const [insuredName, setInsuredName] = React.useState('');
  const [insuredAddress, setInsuredAddress] = React.useState('');
  const [insuredCity, setInsuredCity] = React.useState('');
  const [insuredState, setInsuredState] = React.useState('');
  const [insuredZip, setInsuredZip] = React.useState('');

  React.useEffect(() => {
    if (!client) return;
    setInsuredName(client.name || '');
    setInsuredAddress(client.addressLine1 || '');
    setInsuredCity(client.city || '');
    setInsuredState(client.state || '');
    setInsuredZip(client.zip || '');
    setAcord101NamedInsured(client.name || '');
  }, [client]);
  const [insurerA, setInsurerA] = React.useState('');
  const [insurerB, setInsurerB] = React.useState('');
  const [insurerC, setInsurerC] = React.useState('');
  const [insurerD, setInsurerD] = React.useState('');
  const [insurerE, setInsurerE] = React.useState('');
  const [insurerF, setInsurerF] = React.useState('');
  const [insurerAId, setInsurerAId] = React.useState<string | null>(null);
  const [insurerBId, setInsurerBId] = React.useState<string | null>(null);
  const [insurerCId, setInsurerCId] = React.useState<string | null>(null);
  const [insurerDId, setInsurerDId] = React.useState<string | null>(null);
  const [insurerEId, setInsurerEId] = React.useState<string | null>(null);
  const [insurerFId, setInsurerFId] = React.useState<string | null>(null);
  const [certDate, setCertDate] = React.useState<Date | null>(new Date());
  const [certNumber, setCertNumber] = React.useState('');
  const [revisionNumber, setRevisionNumber] = React.useState('');
  const [locationProperty, setLocationProperty] = React.useState('');
  const [selectedPropertyId, setSelectedPropertyId] = React.useState('');
  const [descriptionOfProperty, setDescriptionOfProperty] = React.useState('');

  // Properties API — fetches all properties for the dropdown; also used for policy-filtered prefill
  const [propertyOptions, setPropertyOptions] = React.useState<{ label: string; value: string; address: string; policyIds: string[] }[]>([]);
  React.useEffect(() => {
    if (!insuredId || !selectedPolicies?.length) {
      setPropertyOptions([]);
      return;
    }
    const ncToken = localStorage.getItem('NCToken') || '';
    const igToken = localStorage.getItem('igtoken') || '';
    fetch(
      `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-properties?top=100&insuredDatabaseId=${encodeURIComponent(insuredId)}&policyIds=${encodeURIComponent(selectedPolicies.join(','))}`,
      { headers: { Authorization: `Bearer ${igToken}`, NCToken: ncToken, 'Content-Type': 'application/json' } }
    )
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const items = Array.isArray(data?.value) ? data.value : [];
        setPropertyOptions(items.map((p: any) => {
          const parts = [p.addressLine1, p.city, p.state, p.zip].filter(Boolean);
          const label = parts.join(', ') || p.description || p.databaseId;
          const address = parts.join('\n');
          return { label, value: p.databaseId, address, policyIds: Array.isArray(p.policyIds) ? p.policyIds : [] };
        }));
      })
      .catch(() => setPropertyOptions([]));
  }, [insuredId, selectedPolicies]);

  // Prefill description from the selected location in the General section
  React.useEffect(() => {
    if (editPrefillPending.current) return;
    setDescriptionOfProperty(selectedLocationLabel);
  }, [selectedLocationLabel]);

  // Auto-prefill "Location of Premises / Description of Property" from policies' linked properties.
  // Backend filters properties by policyIds — we only receive matching properties.
  // Tracks last auto-filled value so policy changes update the field without clobbering manual edits.
  const lastAutoPrefillLocationRef = React.useRef('');
  React.useEffect(() => {
    if (!insuredId) return;
    if (editPrefillPending.current) return;
    if (!selectedPolicies?.length) {
      setDescriptionOfProperty(prev => prev === lastAutoPrefillLocationRef.current ? '' : prev);
      lastAutoPrefillLocationRef.current = '';
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicies, insuredId]);

  const [propInsurerLtr, setPropInsurerLtr] = React.useState('');
  const [propChecked, setPropChecked] = React.useState(false);
  const [propPolicyNumber, setPropPolicyNumber] = React.useState('');
  const [propEffDate, setPropEffDate] = React.useState<Date | null>(null);
  const [propExpDate, setPropExpDate] = React.useState<Date | null>(null);
  const [propBasic, setPropBasic] = React.useState(false);
  const [propBroad, setPropBroad] = React.useState(false);
  const [propSpecial, setPropSpecial] = React.useState(false);
  const [propEarthquake, setPropEarthquake] = React.useState(false);
  const [propWind, setPropWind] = React.useState(false);
  const [propFlood, setPropFlood] = React.useState(false);
  const [propDeductBuilding, setPropDeductBuilding] = React.useState('');
  const [propDeductContents, setPropDeductContents] = React.useState('');
  const [propEarthquakeDeduct, setPropEarthquakeDeduct] = React.useState('');
  const [propWindDeduct, setPropWindDeduct] = React.useState('');
  const [propFloodDeduct, setPropFloodDeduct] = React.useState('');
  const [propCovBuilding, setPropCovBuilding] = React.useState(false);
  const [propLimBuilding, setPropLimBuilding] = React.useState('');
  const [propCovPersonalProp, setPropCovPersonalProp] = React.useState(false);
  const [propLimPersonalProp, setPropLimPersonalProp] = React.useState('');
  const [propCovBusinessIncome, setPropCovBusinessIncome] = React.useState(false);
  const [propLimBusinessIncome, setPropLimBusinessIncome] = React.useState('');
  const [propCovExtraExpense, setPropCovExtraExpense] = React.useState(false);
  const [propLimExtraExpense, setPropLimExtraExpense] = React.useState('');
  const [propCovRentalValue, setPropCovRentalValue] = React.useState(false);
  const [propLimRentalValue, setPropLimRentalValue] = React.useState('');
  const [propCovBlanketBuilding, setPropCovBlanketBuilding] = React.useState(false);
  const [propLimBlanketBuilding, setPropLimBlanketBuilding] = React.useState('');
  const [propCovBlanketPersProp, setPropCovBlanketPersProp] = React.useState(false);
  const [propLimBlanketPersProp, setPropLimBlanketPersProp] = React.useState('');
  const [propCovBlanketBldgPP, setPropCovBlanketBldgPP] = React.useState(false);
  const [propLimBlanketBldgPP, setPropLimBlanketBldgPP] = React.useState('');

  // Property — "other" cause-of-loss rows
  const [propOther1, setPropOther1] = React.useState(false);
  const [propOther1Text, setPropOther1Text] = React.useState('');
  const [propOther1Deduct, setPropOther1Deduct] = React.useState('');
  const [propOther2, setPropOther2] = React.useState(false);
  const [propOther2Text, setPropOther2Text] = React.useState('');
  const [propOther2Deduct, setPropOther2Deduct] = React.useState('');
  // Property — extra "other" covered-property rows
  const [propOtherCov1, setPropOtherCov1] = React.useState(false);
  const [propOtherCov1Desc, setPropOtherCov1Desc] = React.useState('');
  const [propOtherCov1Lim, setPropOtherCov1Lim] = React.useState('');
  const [propOtherCov2, setPropOtherCov2] = React.useState(false);
  const [propOtherCov2Desc, setPropOtherCov2Desc] = React.useState('');
  const [propOtherCov2Lim, setPropOtherCov2Lim] = React.useState('');

  // Inland Marine
  const [inlandInsurerLtr, setInlandInsurerLtr] = React.useState('');
  const [inlandChecked, setInlandChecked] = React.useState(false);
  const [inlandNamedPerils, setInlandNamedPerils] = React.useState(false);
  const [inlandOtherPerils, setInlandOtherPerils] = React.useState(false);
  const [inlandOtherPerilsText, setInlandOtherPerilsText] = React.useState('');
  const [inlandTypeOfPolicy, setInlandTypeOfPolicy] = React.useState('');
  const [inlandPolicyNumber, setInlandPolicyNumber] = React.useState('');
  const [inlandEffDate, setInlandEffDate] = React.useState<Date | null>(null);
  const [inlandExpDate, setInlandExpDate] = React.useState<Date | null>(null);
  // Coverage rows A–D (each has indicator + description + limit)
  const [inlandCov1, setInlandCov1] = React.useState(false);
  const [inlandCov1Desc, setInlandCov1Desc] = React.useState('');
  const [inlandCov1Lim, setInlandCov1Lim] = React.useState('');
  const [inlandCov2, setInlandCov2] = React.useState(false);
  const [inlandCov2Desc, setInlandCov2Desc] = React.useState('');
  const [inlandCov2Lim, setInlandCov2Lim] = React.useState('');
  const [inlandCov3, setInlandCov3] = React.useState(false);
  const [inlandCov3Desc, setInlandCov3Desc] = React.useState('');
  const [inlandCov3Lim, setInlandCov3Lim] = React.useState('');
  const [inlandCov4, setInlandCov4] = React.useState(false);
  const [inlandCov4Desc, setInlandCov4Desc] = React.useState('');
  const [inlandCov4Lim, setInlandCov4Lim] = React.useState('');

  // Crime
  const [crimeInsurerLtr, setCrimeInsurerLtr] = React.useState('');
  const [crimeChecked, setCrimeChecked] = React.useState(false);
  const [crimeTypeOfPolicy, setCrimeTypeOfPolicy] = React.useState('');
  const [crimeCausesOfLoss, setCrimeCausesOfLoss] = React.useState('');
  const [crimePolicyNumber, setCrimePolicyNumber] = React.useState('');
  const [crimeEffDate, setCrimeEffDate] = React.useState<Date | null>(null);
  const [crimeExpDate, setCrimeExpDate] = React.useState<Date | null>(null);
  // Coverage rows A–C
  const [crimeCov1, setCrimeCov1] = React.useState(false);
  const [crimeCov1Desc, setCrimeCov1Desc] = React.useState('');
  const [crimeCov1Lim, setCrimeCov1Lim] = React.useState('');
  const [crimeCov2, setCrimeCov2] = React.useState(false);
  const [crimeCov2Desc, setCrimeCov2Desc] = React.useState('');
  const [crimeCov2Lim, setCrimeCov2Lim] = React.useState('');
  const [crimeCov3, setCrimeCov3] = React.useState(false);
  const [crimeCov3Desc, setCrimeCov3Desc] = React.useState('');
  const [crimeCov3Lim, setCrimeCov3Lim] = React.useState('');

  // Boiler & Machinery — coverage rows A–B
  const [boilerInsurerLtr, setBoilerInsurerLtr] = React.useState('');
  const [boilerChecked, setBoilerChecked] = React.useState(false);
  const [boilerPolicyNumber, setBoilerPolicyNumber] = React.useState('');
  const [boilerEffDate, setBoilerEffDate] = React.useState<Date | null>(null);
  const [boilerExpDate, setBoilerExpDate] = React.useState<Date | null>(null);
  const [boilerCov1, setBoilerCov1] = React.useState(false);
  const [boilerCov1Desc, setBoilerCov1Desc] = React.useState('');
  const [boilerCov1Lim, setBoilerCov1Lim] = React.useState('');
  const [boilerCov2, setBoilerCov2] = React.useState(false);
  const [boilerCov2Desc, setBoilerCov2Desc] = React.useState('');
  const [boilerCov2Lim, setBoilerCov2Lim] = React.useState('');

  // Other — insurer letter + coverage rows
  const [otherInsurerLtr, setOtherInsurerLtr] = React.useState('');
  const [otherType, setOtherType] = React.useState('');
  const [otherPolicyNumber, setOtherPolicyNumber] = React.useState('');
  const [otherEffDate, setOtherEffDate] = React.useState<Date | null>(null);
  const [otherExpDate, setOtherExpDate] = React.useState<Date | null>(null);
  const [otherCov1, setOtherCov1] = React.useState(false);
  const [otherCov1Desc, setOtherCov1Desc] = React.useState('');
  const [otherCov1Lim, setOtherCov1Lim] = React.useState('');
  const [otherCov2, setOtherCov2] = React.useState(false);
  const [otherCov2Desc, setOtherCov2Desc] = React.useState('');
  const [otherCov2Lim, setOtherCov2Lim] = React.useState('');

  const [specialConditions, setSpecialConditions] = React.useState('');

  const [holderName, setHolderName] = React.useState('');
  const [holderAddress, setHolderAddress] = React.useState('');
  const [holderCity, setHolderCity] = React.useState('');
  const [holderStreet, setHolderStreet] = React.useState('');
  const [holderState, setHolderState] = React.useState('');
  const [holderZip, setHolderZip] = React.useState('');
  const [holderSearchQuery, setHolderSearchQuery] = React.useState('');
  const { holders: holderSuggestions } = useCertificateHolders(holderSearchQuery, 10);
  const [holderEnabled, setHolderEnabled] = React.useState(false);
  const [authRepSignature, setAuthRepSignature] = React.useState('');
  const { signatureOptions, refetchSignatures } = useSignatures();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = React.useState('');
  React.useEffect(() => {
    if (!authRepSignature) { setSignaturePreviewUrl(''); return; }
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-preview/${authRepSignature}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(url => setSignaturePreviewUrl(url.replace(/^"|"$/g, '')))
      .catch(() => setSignaturePreviewUrl(''));
  }, [authRepSignature]);

  const [acord101Agency, setAcord101Agency] = React.useState('');
  const [acord101NamedInsured, setAcord101NamedInsured] = React.useState('');
  const [acord101AdditionalRemarks, setAcord101AdditionalRemarks] = React.useState('');

  const editPrefillPending = React.useRef(!!(editCoverages && editCoverages.length > 0));

  // ── Building coverage prefill (from location/property selection) ──────────
  const prefillPropertyFromBuildingCoverages = (returnObjects: Array<{ fieldName: string; value: string }>) => {
    const get = (name: string) => returnObjects.find(o => o.fieldName === name)?.value ?? '';
    const getBool = (name: string) => get(name) === 'Yes';

    setPropChecked(getBool('Policy_PolicyType_PropertyIndicator_A'));
    setPropBasic(getBool('Policy_PolicyType_BasicIndicator_A'));
    setPropBroad(getBool('Policy_PolicyType_BroadIndicator_A'));
    setPropSpecial(getBool('Policy_PolicyType_SpecialIndicator_A'));
    setPropEarthquake(getBool('CommercialPropertyCoverage_EarthquakeOption_IncludedIndicator_A'));
    setPropWind(getBool('Policy_PolicyType_WindIndicator_A'));
    setPropFlood(getBool('CommercialPropertyCoverage_Flood_YesIndicator_A'));
    setPropOther1(getBool('Policy_PolicyType_OtherIndicator_A'));
    setPropOther1Text(get('Policy_PolicyType_OtherDescription_A'));
    setPropOther1Deduct(get('CommercialProperty_Premises_DeductibleAmount_F'));
    setPropOther2(getBool('Policy_PolicyType_OtherIndicator_B'));
    setPropOther2Text(get('Policy_PolicyType_OtherDescription_B'));
    setPropOther2Deduct(get('CommercialProperty_Premises_DeductibleAmount_G'));
    setPropDeductBuilding(get('CommercialProperty_Premises_DeductibleAmount_A'));
    setPropDeductContents(get('CommercialProperty_Premises_DeductibleAmount_B'));
    setPropEarthquakeDeduct(get('CommercialProperty_Premises_DeductibleAmount_C'));
    setPropWindDeduct(get('CommercialProperty_Premises_DeductibleAmount_D'));
    setPropFloodDeduct(get('CommercialProperty_Premises_DeductibleAmount_E'));
    setPropCovBuilding(getBool('Property_Building_CoverageIndicator_A'));
    setPropLimBuilding(get('Property_Building_LimitAmount_A'));
    setPropCovPersonalProp(getBool('Property_PersonalProperty_CoverageIndicator_A'));
    setPropLimPersonalProp(get('Property_PersonalProperty_LimitAmount_A'));
    setPropCovBusinessIncome(getBool('CommercialPropertyCoverage_BusinessIncomeOption_IncludedIndicator_A'));
    setPropLimBusinessIncome(get('CommercialPropertyCoverage_BusinessIncome_LimitAmount_A'));
    setPropCovExtraExpense(getBool('CommercialPropertyCoverage_ExtraExpenseOption_IncludedIndicator_A'));
    setPropLimExtraExpense(get('CommercialPropertyCoverage_ExtraExpense_LimitAmount_A'));
    setPropCovRentalValue(getBool('CommercialPropertyCoverage_RentalValueOption_IncludedIndicator_A'));
    setPropLimRentalValue(get('CommercialPropertyCoverage_RentalValue_LimitAmount_A'));
    setPropCovBlanketBuilding(getBool('CommercialProperty_Blanket_YesIndicator_A'));
    setPropLimBlanketBuilding(get('CommercialProperty_Blanket_LimitAmount_A'));
    setPropCovBlanketPersProp(getBool('CommercialProperty_Blanket_YesIndicator_B'));
    setPropLimBlanketPersProp(get('CommercialProperty_Blanket_LimitAmount_B'));
    setPropCovBlanketBldgPP(getBool('CommercialProperty_Blanket_YesIndicator_C'));
    setPropLimBlanketBldgPP(get('CommercialProperty_Blanket_LimitAmount_C'));
    setPropOtherCov1(getBool('PropertyCoverage_OtherOption_IncludedIndicator_A'));
    setPropOtherCov1Desc(get('PropertyCoverage_Other_CoverageDescription_A'));
    setPropOtherCov1Lim(get('PropertyCoverage_Other_LimitAmount_A'));
    setPropOtherCov2(getBool('PropertyCoverage_OtherOption_IncludedIndicator_B'));
    setPropOtherCov2Desc(get('PropertyCoverage_Other_CoverageDescription_B'));
    setPropOtherCov2Lim(get('PropertyCoverage_Other_LimitAmount_B'));
    const policyNumber = get('Policy_Property_PolicyNumberIdentifier_A');
    const effDateStr = get('Policy_Property_EffectiveDate_A');
    const expDateStr = get('Policy_Property_ExpirationDate_A');
    if (policyNumber) setPropPolicyNumber(policyNumber);
    if (effDateStr) setPropEffDate(new Date(effDateStr));
    if (expDateStr) setPropExpDate(new Date(expDateStr));
  };

  // ── Coverage prefill ───────────────────────────────────────────────────────
  const clearCoverages = () => {
    setInsurerA(''); setInsurerB(''); setInsurerC('');
    setInsurerD(''); setInsurerE(''); setInsurerF('');
    setInsurerAId(null); setInsurerBId(null); setInsurerCId(null);
    setInsurerDId(null); setInsurerEId(null); setInsurerFId(null);
    // Property
    setPropInsurerLtr(''); setPropChecked(false);
    setPropPolicyNumber(''); setPropEffDate(null); setPropExpDate(null);
    setPropBasic(false); setPropBroad(false); setPropSpecial(false);
    setPropEarthquake(false); setPropWind(false); setPropFlood(false);
    setPropDeductBuilding(''); setPropDeductContents('');
    setPropEarthquakeDeduct(''); setPropWindDeduct(''); setPropFloodDeduct('');
    setPropOther1(false); setPropOther1Text(''); setPropOther1Deduct('');
    setPropOther2(false); setPropOther2Text(''); setPropOther2Deduct('');
    setPropCovBuilding(false); setPropLimBuilding('');
    setPropCovPersonalProp(false); setPropLimPersonalProp('');
    setPropCovBusinessIncome(false); setPropLimBusinessIncome('');
    setPropCovExtraExpense(false); setPropLimExtraExpense('');
    setPropCovRentalValue(false); setPropLimRentalValue('');
    setPropCovBlanketBuilding(false); setPropLimBlanketBuilding('');
    setPropCovBlanketPersProp(false); setPropLimBlanketPersProp('');
    setPropCovBlanketBldgPP(false); setPropLimBlanketBldgPP('');
    setPropOtherCov1(false); setPropOtherCov1Desc(''); setPropOtherCov1Lim('');
    setPropOtherCov2(false); setPropOtherCov2Desc(''); setPropOtherCov2Lim('');
    // Inland Marine
    setInlandInsurerLtr(''); setInlandChecked(false);
    setInlandNamedPerils(false); setInlandOtherPerils(false); setInlandOtherPerilsText('');
    setInlandTypeOfPolicy(''); setInlandPolicyNumber('');
    setInlandEffDate(null); setInlandExpDate(null);
    setInlandCov1(false); setInlandCov1Desc(''); setInlandCov1Lim('');
    setInlandCov2(false); setInlandCov2Desc(''); setInlandCov2Lim('');
    setInlandCov3(false); setInlandCov3Desc(''); setInlandCov3Lim('');
    setInlandCov4(false); setInlandCov4Desc(''); setInlandCov4Lim('');
    // Crime
    setCrimeInsurerLtr(''); setCrimeChecked(false);
    setCrimeTypeOfPolicy(''); setCrimeCausesOfLoss('');
    setCrimePolicyNumber(''); setCrimeEffDate(null); setCrimeExpDate(null);
    setCrimeCov1(false); setCrimeCov1Desc(''); setCrimeCov1Lim('');
    setCrimeCov2(false); setCrimeCov2Desc(''); setCrimeCov2Lim('');
    setCrimeCov3(false); setCrimeCov3Desc(''); setCrimeCov3Lim('');
    // Boiler
    setBoilerInsurerLtr(''); setBoilerChecked(false);
    setBoilerPolicyNumber(''); setBoilerEffDate(null); setBoilerExpDate(null);
    setBoilerCov1(false); setBoilerCov1Desc(''); setBoilerCov1Lim('');
    setBoilerCov2(false); setBoilerCov2Desc(''); setBoilerCov2Lim('');
    // Other
    setOtherInsurerLtr(''); setOtherType(''); setOtherPolicyNumber('');
    setOtherEffDate(null); setOtherExpDate(null);
    setOtherCov1(false); setOtherCov1Desc(''); setOtherCov1Lim('');
    setOtherCov2(false); setOtherCov2Desc(''); setOtherCov2Lim('');
    setSpecialConditions('');
    setSelectedPropertyId('');
  };

  const prefillFromApi = (data: AcordFieldItem[]) => {
    const ins = getInsurers(data);
    setInsurerA(ins.A.name); setInsurerB(ins.B.name); setInsurerC(ins.C.name);
    setInsurerD(ins.D.name); setInsurerE(ins.E.name); setInsurerF(ins.F.name);
    // Resolve carrier database IDs by name so NaicId is populated on create
    const apiSlots: Array<[string, (id: string | null) => void]> = [
      [ins.A.name, setInsurerAId], [ins.B.name, setInsurerBId],
      [ins.C.name, setInsurerCId], [ins.D.name, setInsurerDId],
      [ins.E.name, setInsurerEId], [ins.F.name, setInsurerFId],
    ];
    apiSlots.forEach(([name, setter]) => {
      if (name) resolveCarrierIdByName(name).then(id => { if (id) setter(id); });
    });

    const prop = mapFields(data, PROPERTY_MAP);
    if (prop.policyNumber) setPropPolicyNumber(prop.policyNumber);
    if (prop.effDate)      setPropEffDate(prop.effDate);
    if (prop.expDate)      setPropExpDate(prop.expDate);

    if (!isV2016 && !showAcord101) {
      if (prop.insurerLtr) setPropInsurerLtr(prop.insurerLtr);
      setPropChecked(prop.checked);
      setPropBasic(prop.basic);       setPropBroad(prop.broad);         setPropSpecial(prop.special);
      setPropEarthquake(prop.earthquake); setPropWind(prop.wind);      setPropFlood(prop.flood);
      setPropOther1(prop.other1); setPropOther1Text(prop.other1Text); setPropOther1Deduct(prop.other1Deduct);
      setPropOther2(prop.other2); setPropOther2Text(prop.other2Text); setPropOther2Deduct(prop.other2Deduct);
      setPropDeductBuilding(prop.deductBuilding); setPropDeductContents(prop.deductContents);
      setPropEarthquakeDeduct(prop.earthquakeDeduct); setPropWindDeduct(prop.windDeduct); setPropFloodDeduct(prop.floodDeduct);
      setPropCovBuilding(prop.covBuilding);       setPropLimBuilding(prop.limBuilding);
      setPropCovPersonalProp(prop.covPersonalProp); setPropLimPersonalProp(prop.limPersonalProp);
      setPropCovBusinessIncome(prop.covBusinessIncome); setPropLimBusinessIncome(prop.limBusinessIncome);
      setPropCovExtraExpense(prop.covExtraExpense);     setPropLimExtraExpense(prop.limExtraExpense);
      setPropCovRentalValue(prop.covRentalValue);       setPropLimRentalValue(prop.limRentalValue);
      setPropCovBlanketBuilding(prop.covBlanketBuilding); setPropLimBlanketBuilding(prop.limBlanketBuilding);
      setPropCovBlanketPersProp(prop.covBlanketPersProp); setPropLimBlanketPersProp(prop.limBlanketPersProp);
      setPropCovBlanketBldgPP(prop.covBlanketBldgPP);     setPropLimBlanketBldgPP(prop.limBlanketBldgPP);
      setPropOtherCov1(prop.otherCov1); setPropOtherCov1Desc(prop.otherCov1Desc); setPropOtherCov1Lim(prop.otherCov1Lim);
      setPropOtherCov2(prop.otherCov2); setPropOtherCov2Desc(prop.otherCov2Desc); setPropOtherCov2Lim(prop.otherCov2Lim);
    }

    const inland = mapFields(data, INLAND_MAP);
    if (inland.insurerLtr) setInlandInsurerLtr(inland.insurerLtr);
    setInlandChecked(inland.checked);
    setInlandNamedPerils(inland.namedPerils);
    setInlandOtherPerils(inland.otherPerils); setInlandOtherPerilsText(inland.otherPerilsText);
    setInlandTypeOfPolicy(inland.typeOfPolicy);
    setInlandPolicyNumber(inland.policyNumber);
    setInlandEffDate(inland.effDate); setInlandExpDate(inland.expDate);
    setInlandCov1(inland.cov1); setInlandCov1Desc(inland.cov1Desc); setInlandCov1Lim(inland.cov1Lim);
    setInlandCov2(inland.cov2); setInlandCov2Desc(inland.cov2Desc); setInlandCov2Lim(inland.cov2Lim);
    setInlandCov3(inland.cov3); setInlandCov3Desc(inland.cov3Desc); setInlandCov3Lim(inland.cov3Lim);
    setInlandCov4(inland.cov4); setInlandCov4Desc(inland.cov4Desc); setInlandCov4Lim(inland.cov4Lim);

    const crime = mapFields(data, CRIME_MAP);
    if (crime.insurerLtr) setCrimeInsurerLtr(crime.insurerLtr);
    setCrimeChecked(crime.checked);
    setCrimeTypeOfPolicy(crime.typeOfPolicy);
    setCrimePolicyNumber(crime.policyNumber);
    setCrimeEffDate(crime.effDate); setCrimeExpDate(crime.expDate);
    setCrimeCov1(crime.cov1); setCrimeCov1Desc(crime.cov1Desc); setCrimeCov1Lim(crime.cov1Lim);
    setCrimeCov2(crime.cov2); setCrimeCov2Desc(crime.cov2Desc); setCrimeCov2Lim(crime.cov2Lim);
    setCrimeCov3(crime.cov3); setCrimeCov3Desc(crime.cov3Desc); setCrimeCov3Lim(crime.cov3Lim);

    const boiler = mapFields(data, BOILER_MAP);
    if (boiler.insurerLtr) setBoilerInsurerLtr(boiler.insurerLtr);
    setBoilerChecked(boiler.checked);
    setBoilerPolicyNumber(boiler.policyNumber);
    setBoilerEffDate(boiler.effDate); setBoilerExpDate(boiler.expDate);
    setBoilerCov1(boiler.cov1); setBoilerCov1Desc(boiler.cov1Desc); setBoilerCov1Lim(boiler.cov1Lim);
    setBoilerCov2(boiler.cov2); setBoilerCov2Desc(boiler.cov2Desc); setBoilerCov2Lim(boiler.cov2Lim);

    const other = mapFields(data, OTHER24_MAP);
    if (other.insurerLtr) setOtherInsurerLtr(other.insurerLtr);
    setOtherType(other.type);
    setOtherPolicyNumber(other.policyNumber);
    setOtherEffDate(other.effDate); setOtherExpDate(other.expDate);
    setOtherCov1(other.cov1); setOtherCov1Desc(other.cov1Desc); setOtherCov1Lim(other.cov1Lim);
    setOtherCov2(other.cov2); setOtherCov2Desc(other.cov2Desc); setOtherCov2Lim(other.cov2Lim);

    setSpecialConditions(getAllRemarkLines(data, 'CertificateOfLiabilityInsurance_ACORDForm_RemarkText_B'));
  };

  React.useEffect(() => {
    clearCoverages();
    if (!selectedPolicies || selectedPolicies.length === 0) return;

    if (editPrefillPending.current) {
      editPrefillPending.current = false;
      if (editCoverages && editCoverages.length > 0) {
        const p = parseAcord24StoredCoverages(editCoverages, editPolicies || []);
        setInsurerA(p.insurerA); setInsurerB(p.insurerB); setInsurerC(p.insurerC);
        setInsurerD(p.insurerD); setInsurerE(p.insurerE); setInsurerF(p.insurerF);
        setInsurerAId(p.insurerAId ?? null); setInsurerBId(p.insurerBId ?? null); setInsurerCId(p.insurerCId ?? null);
        setInsurerDId(p.insurerDId ?? null); setInsurerEId(p.insurerEId ?? null); setInsurerFId(p.insurerFId ?? null);
        // For old certs with null stored naiCid, resolve IDs by carrier name from the dropdown cache
        const slotNames: Array<[string, (id: string | null) => void]> = [
          [p.insurerA, setInsurerAId], [p.insurerB, setInsurerBId],
          [p.insurerC, setInsurerCId], [p.insurerD, setInsurerDId],
          [p.insurerE, setInsurerEId], [p.insurerF, setInsurerFId],
        ];
        const idsFromPrefill = [p.insurerAId, p.insurerBId, p.insurerCId, p.insurerDId, p.insurerEId, p.insurerFId];
        slotNames.forEach(([name, setter], i) => {
          if (name && !idsFromPrefill[i]) {
            resolveCarrierIdByName(name).then(id => { if (id) setter(id); });
          }
        });
        if (p.certDate)       setCertDate(p.certDate);
        if (p.certNumber)     setCertNumber(p.certNumber);
        if (p.revisionNumber) setRevisionNumber(p.revisionNumber);
        setLocationProperty(p.locationProperty || selectedLocationLabel);
        setDescriptionOfProperty(p.descriptionOfProperty || p.locationProperty || selectedLocationLabel);
        setSpecialConditions(p.specialConditions);
        if (p.contactName)      setContactName(p.contactName);
        if (p.contactPhone)     setContactPhone(p.contactPhone);
        if (p.contactFax)       setContactFax(p.contactFax);
        if (p.contactEmail)     setContactEmail(p.contactEmail);
        if (p.producerCustomerId) setProducerCustomerId(p.producerCustomerId);
        // Section-enable flags
        setPropChecked(p.propChecked);
        setInlandChecked(p.inlandChecked);
        setCrimeChecked(p.crimeChecked);
        setBoilerChecked(p.boilerChecked);
        // Property
        setPropInsurerLtr(p.propInsurerLtr); setPropPolicyNumber(p.propPolicyNumber);
        setPropEffDate(p.propEffDate); setPropExpDate(p.propExpDate);
        setPropBasic(p.propBasic); setPropBroad(p.propBroad); setPropSpecial(p.propSpecial);
        setPropEarthquake(p.propEarthquake); setPropWind(p.propWind); setPropFlood(p.propFlood);
        setPropDeductBuilding(p.propDeductBuilding); setPropDeductContents(p.propDeductContents);
        setPropEarthquakeDeduct(p.propEarthquakeDeduct); setPropWindDeduct(p.propWindDeduct);
        setPropFloodDeduct(p.propFloodDeduct);
        setPropOther1(p.propOther1); setPropOther1Text(p.propOther1Text); setPropOther1Deduct(p.propOther1Deduct);
        setPropOther2(p.propOther2); setPropOther2Text(p.propOther2Text); setPropOther2Deduct(p.propOther2Deduct);
        setPropCovBuilding(p.propCovBuilding); setPropLimBuilding(p.propLimBuilding);
        setPropCovPersonalProp(p.propCovPersonalProp); setPropLimPersonalProp(p.propLimPersonalProp);
        setPropCovBusinessIncome(p.propCovBusinessIncome); setPropLimBusinessIncome(p.propLimBusinessIncome);
        setPropCovExtraExpense(p.propCovExtraExpense); setPropLimExtraExpense(p.propLimExtraExpense);
        setPropCovRentalValue(p.propCovRentalValue); setPropLimRentalValue(p.propLimRentalValue);
        setPropCovBlanketBuilding(p.propCovBlanketBuilding); setPropLimBlanketBuilding(p.propLimBlanketBuilding);
        setPropCovBlanketPersProp(p.propCovBlanketPersProp); setPropLimBlanketPersProp(p.propLimBlanketPersProp);
        setPropCovBlanketBldgPP(p.propCovBlanketBldgPP); setPropLimBlanketBldgPP(p.propLimBlanketBldgPP);
        setPropOtherCov1(p.propOtherCov1); setPropOtherCov1Desc(p.propOtherCov1Desc); setPropOtherCov1Lim(p.propOtherCov1Lim);
        setPropOtherCov2(p.propOtherCov2); setPropOtherCov2Desc(p.propOtherCov2Desc); setPropOtherCov2Lim(p.propOtherCov2Lim);
        // Inland
        setInlandInsurerLtr(p.inlandInsurerLtr); setInlandPolicyNumber(p.inlandPolicyNumber);
        setInlandEffDate(p.inlandEffDate); setInlandExpDate(p.inlandExpDate);
        setInlandNamedPerils(p.inlandNamedPerils); setInlandOtherPerils(p.inlandOtherPerils);
        setInlandOtherPerilsText(p.inlandOtherPerilsText); setInlandTypeOfPolicy(p.inlandTypeOfPolicy);
        setInlandCov1(p.inlandCov1); setInlandCov1Desc(p.inlandCov1Desc); setInlandCov1Lim(p.inlandCov1Lim);
        setInlandCov2(p.inlandCov2); setInlandCov2Desc(p.inlandCov2Desc); setInlandCov2Lim(p.inlandCov2Lim);
        setInlandCov3(p.inlandCov3); setInlandCov3Desc(p.inlandCov3Desc); setInlandCov3Lim(p.inlandCov3Lim);
        setInlandCov4(p.inlandCov4); setInlandCov4Desc(p.inlandCov4Desc); setInlandCov4Lim(p.inlandCov4Lim);
        // Crime
        setCrimeInsurerLtr(p.crimeInsurerLtr); setCrimePolicyNumber(p.crimePolicyNumber);
        setCrimeEffDate(p.crimeEffDate); setCrimeExpDate(p.crimeExpDate);
        setCrimeTypeOfPolicy(p.crimeTypeOfPolicy);
        setCrimeCov1(p.crimeCov1); setCrimeCov1Desc(p.crimeCov1Desc); setCrimeCov1Lim(p.crimeCov1Lim);
        setCrimeCov2(p.crimeCov2); setCrimeCov2Desc(p.crimeCov2Desc); setCrimeCov2Lim(p.crimeCov2Lim);
        setCrimeCov3(p.crimeCov3); setCrimeCov3Desc(p.crimeCov3Desc); setCrimeCov3Lim(p.crimeCov3Lim);
        // Boiler
        setBoilerInsurerLtr(p.boilerInsurerLtr); setBoilerPolicyNumber(p.boilerPolicyNumber);
        setBoilerEffDate(p.boilerEffDate); setBoilerExpDate(p.boilerExpDate);
        setBoilerCov1(p.boilerCov1); setBoilerCov1Desc(p.boilerCov1Desc); setBoilerCov1Lim(p.boilerCov1Lim);
        setBoilerCov2(p.boilerCov2); setBoilerCov2Desc(p.boilerCov2Desc); setBoilerCov2Lim(p.boilerCov2Lim);
        // Other
        setOtherInsurerLtr(p.otherInsurerLtr); setOtherType(p.otherType);
        setOtherPolicyNumber(p.otherPolicyNumber);
        setOtherEffDate(p.otherEffDate); setOtherExpDate(p.otherExpDate);
        setOtherCov1(p.otherCov1); setOtherCov1Desc(p.otherCov1Desc); setOtherCov1Lim(p.otherCov1Lim);
        setOtherCov2(p.otherCov2); setOtherCov2Desc(p.otherCov2Desc); setOtherCov2Lim(p.otherCov2Lim);
        if (p.acord101AdditionalRemarks) setAcord101AdditionalRemarks(p.acord101AdditionalRemarks);
        if (p.authRepSignature) setAuthRepSignature(p.authRepSignature);
        if (p.holderName) {
          setHolderEnabled(true);
          setHolderName(p.holderName);
          setHolderAddress(p.holderAddress);
          setHolderCity(p.holderCity);
          setHolderStreet(p.holderStreet);
          setHolderState(p.holderState);
          setHolderZip(p.holderZip);
        }
      }
      onLoadingChange?.(false);
      return;
    }

    let cancelled = false;
    const fetchCoverages = async () => {
      onLoadingChange?.(true);
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';
        const res = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/get-coverages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'NCToken': ncToken,
              Authorization: `Bearer ${igToken}`,
            },
            body: JSON.stringify({
              policiesIds: selectedPolicies,
              truckingCompanyId: insuredId || '',
              fileParentId: '5A7BD49C-B7A8-4ABF-B713-31892FDE171A',
              naics: [{ Code: '', Name: '' }],
              Page: 'certificateFiller',
            }),
          }
        );
        if (cancelled) return;
        const json = await res.json();
        const arr: AcordFieldItem[] = Array.isArray(json.Data) ? json.Data : [];
        if (arr.length > 0) prefillFromApi(arr);
      } catch {
        // silently fail — user can still fill manually
      } finally {
        if (!cancelled) onLoadingChange?.(false);
      }
    };
    fetchCoverages();
    return () => { cancelled = true; onLoadingChange?.(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicies, insuredId]);

  // Prefill contact section from the first selected policy's agent
  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;
    fetchPolicyAgentContact(selectedPolicies[0]).then(async ({ agentId, name, phone, fax, email }) => {
      if (name) setContactName(name);
      if (phone) setContactPhone(phone);
      if (fax) setContactFax(fax);
      if (email) setContactEmail(email);
      if (agentId && name) {
        const { npnNumber, addressLine1, city, state, zipCode } = await fetchAgentContact(agentId);
        setProducerAddress(npnNumber ? `${name} (${npnNumber})` : name);
        if (addressLine1) setProducerStreet(addressLine1);
        if (city) setProducerCity(city);
        if (state) setProducerState(state);
        if (zipCode) setProducerZip(zipCode);
        const agencyParts = [name, addressLine1, city, state, zipCode].filter(Boolean);
        setAcord101Agency(agencyParts.join(', '));
      }
    });
  }, [selectedPolicies]);


  React.useImperativeHandle(ref, () => ({
    applyBuildingCoverages(returnObjects: Array<{ fieldName: string; value: string }>) {
      prefillPropertyFromBuildingCoverages(returnObjects);
    },
    getFormData: (): Acord24FormData => {
      // Synchronous fallback: if state ID is still null (async lookup pending),
      // read directly from the already-loaded dropdown cache
      const resolvedAId = insurerAId ?? lookupCarrierIdSync(insurerA);
      const resolvedBId = insurerBId ?? lookupCarrierIdSync(insurerB);
      const resolvedCId = insurerCId ?? lookupCarrierIdSync(insurerC);
      const resolvedDId = insurerDId ?? lookupCarrierIdSync(insurerD);
      const resolvedEId = insurerEId ?? lookupCarrierIdSync(insurerE);
      const resolvedFId = insurerFId ?? lookupCarrierIdSync(insurerF);
      return ({
      producerAddress, producerCity, producerStreet, producerState, producerZip,
      contactName, contactPhone, contactFax, contactEmail, producerCustomerId,
      insuredName, insuredAddress, insuredCity, insuredState, insuredZip,
      insurerA, insurerB, insurerC, insurerD, insurerE, insurerF,
      insurerAId: resolvedAId, insurerBId: resolvedBId, insurerCId: resolvedCId,
      insurerDId: resolvedDId, insurerEId: resolvedEId, insurerFId: resolvedFId,
      certDate, certNumber, revisionNumber, locationProperty, descriptionOfProperty, specialConditions,
      propInsurerLtr, propChecked, propPolicyNumber, propEffDate, propExpDate,
      propBasic, propBroad, propSpecial, propEarthquake, propWind, propFlood,
      propDeductBuilding, propDeductContents,
      propEarthquakeDeduct, propWindDeduct, propFloodDeduct,
      propOther1, propOther1Text, propOther1Deduct, propOther2, propOther2Text, propOther2Deduct,
      propCovBuilding, propLimBuilding,
      propCovPersonalProp, propLimPersonalProp,
      propCovBusinessIncome, propLimBusinessIncome,
      propCovExtraExpense, propLimExtraExpense,
      propCovRentalValue, propLimRentalValue,
      propCovBlanketBuilding, propLimBlanketBuilding,
      propCovBlanketPersProp, propLimBlanketPersProp,
      propCovBlanketBldgPP, propLimBlanketBldgPP,
      propOtherCov1, propOtherCov1Desc, propOtherCov1Lim,
      propOtherCov2, propOtherCov2Desc, propOtherCov2Lim,
      inlandInsurerLtr, inlandChecked, inlandNamedPerils, inlandOtherPerils, inlandOtherPerilsText,
      inlandTypeOfPolicy, inlandPolicyNumber, inlandEffDate, inlandExpDate,
      inlandCov1, inlandCov1Desc, inlandCov1Lim,
      inlandCov2, inlandCov2Desc, inlandCov2Lim,
      inlandCov3, inlandCov3Desc, inlandCov3Lim,
      inlandCov4, inlandCov4Desc, inlandCov4Lim,
      crimeInsurerLtr, crimeChecked, crimeTypeOfPolicy, crimeCausesOfLoss, crimePolicyNumber, crimeEffDate, crimeExpDate,
      crimeCov1, crimeCov1Desc, crimeCov1Lim,
      crimeCov2, crimeCov2Desc, crimeCov2Lim,
      crimeCov3, crimeCov3Desc, crimeCov3Lim,
      boilerInsurerLtr, boilerChecked, boilerPolicyNumber, boilerEffDate, boilerExpDate,
      boilerCov1, boilerCov1Desc, boilerCov1Lim,
      boilerCov2, boilerCov2Desc, boilerCov2Lim,
      otherInsurerLtr, otherType, otherPolicyNumber, otherEffDate, otherExpDate,
      otherCov1, otherCov1Desc, otherCov1Lim,
      otherCov2, otherCov2Desc, otherCov2Lim,
      holderName, holderAddress, holderCity, holderStreet, holderState, holderZip,
      authRepSignature,
      showAcord101,
      acord101Agency, acord101NamedInsured, acord101AdditionalRemarks,
    });
    },
  }));

  return (
    <>
      <div className="masterCert__section">
        {/* ACORD HEADER */}
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">CERTIFICATE OF PROPERTY INSURANCE</div>
          </div>
          <div className="masterCert__dateWrapper">
            <div className="masterCert__dateLabel">DATE (MM/DD/YYYY)</div>
            <div className="masterCert__dateCell">
              <Calendar value={certDate} onChange={(e) => setCertDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
          </div>
        </div>

        {/* DISCLAIMER */}
        <div className="masterCert__disclaimer">
          This certificate is issued as a matter of information only and confers no rights upon the certificate holder. This certificate does not affirmatively or negatively amend, extend or alter the coverage afforded by the policies below. This certificate of insurance does not constitute a contract between the issuing insurer(s), authorized representative or producer, and the certificate holder.
        </div>
        <div className="masterCert__disclaimerImportant">
          If this certificate is being prepared for a party who has an insurable interest in the property, do not use this form. Use ACORD 27 or ACORD 28.
        </div>

        {/* PRODUCER & CONTACT / INSURED & INSURERS */}
        <div className="masterCert__twoColumnLayout">
          {/* LEFT COLUMN */}
          <div>
            {/* PRODUCER */}
            <ProducerSection
              address={producerAddress}
              onAddressChange={setProducerAddress}
              city={producerCity}
              onCityChange={setProducerCity}
              street={producerStreet}
              onStreetChange={setProducerStreet}
              state={producerState}
              onStateChange={setProducerState}
              zip={producerZip}
              onZipChange={setProducerZip}
              onProducerSelect={(p) => {
                const parts = [p.businessName, p.adressLine1, p.city, p.state, p.zip].filter(Boolean);
                setAcord101Agency(parts.join(', '));
              }}
              onAgentContact={(phone, fax, email) => { setContactPhone(phone); setContactFax(fax); setContactEmail(email); }}
            />

            {/* INSURED */}
            <div>
              <div className="masterCert__sectionHeader">INSURED</div>
              <InputText maxLength={100} value={insuredName} onChange={(e) => setInsuredName(e.target.value)} placeholder="Type insured name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <InputText maxLength={100} value={insuredAddress} onChange={(e) => setInsuredAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={insuredCity} onChange={(e) => setInsuredCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={50} value={insuredState} onChange={(e) => setInsuredState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={20} value={insuredZip} onChange={(e) => setInsuredZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div>
            {/* CONTACT INFO */}
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">CONTACT NAME:</div>
                  <InputText maxLength={100} value={contactName} onChange={(e) => setContactName(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
                <div></div>
              </div>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">PHONE (A/C, No, Ext):</div>
                  <InputText value={formatUsPhone(contactPhone)} onChange={(e) => setContactPhone(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
                <div>
                  <div className="masterCert__contactLabel">FAX (A/C, No):</div>
                  <InputText value={formatUsPhone(contactFax)} onChange={(e) => setContactFax(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
              </div>
              <div style={{ marginBottom: 6 }}>
                <div className="masterCert__contactLabel">E-MAIL ADDRESS:</div>
                <InputText maxLength={100} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
              </div>
              <div>
                <div className="masterCert__contactLabel">PRODUCER CUSTOMER ID #:</div>
                <InputText maxLength={30} value={producerCustomerId} onChange={(e) => setProducerCustomerId(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            {/* INSURERS AFFORDING COVERAGE */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>INSURERS AFFORDING COVERAGE (and NAIC #)</div>
                <Button className="carrierDialog__secondaryBtn" label="Add New" icon="pi pi-plus" outlined size="small" style={{ flexShrink: 0, padding: '2px 10px', fontSize: '0.7rem', height: '28px' }} onClick={() => setShowCreateCarrier(true)} />
              </div>
              <div className="masterCert__insurersGrid">
                <div className="masterCert__insurerLabel">INSURER A:</div>
                <InsurerSearchDropdown value={insurerA} onChange={setInsurerA} onIdChange={setInsurerAId} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER B:</div>
                <InsurerSearchDropdown value={insurerB} onChange={setInsurerB} onIdChange={setInsurerBId} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER C:</div>
                <InsurerSearchDropdown value={insurerC} onChange={setInsurerC} onIdChange={setInsurerCId} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER D:</div>
                <InsurerSearchDropdown value={insurerD} onChange={setInsurerD} onIdChange={setInsurerDId} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER E:</div>
                <InsurerSearchDropdown value={insurerE} onChange={setInsurerE} onIdChange={setInsurerEId} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER F:</div>
                <InsurerSearchDropdown value={insurerF} onChange={setInsurerF} onIdChange={setInsurerFId} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
          </div>
        </div>

        {/* ══════════ COVERAGES ══════════ */}
        <div className="masterCert__coveragesSection">
          <div className="masterCert__coveragesTitle">COVERAGES</div>
          <div className="masterCert__certNumberGrid">
            <div>
              <div className="masterCert__contactLabel">CERTIFICATE NUMBER:</div>
              <InputText maxLength={30} value={certNumber} onChange={(e) => setCertNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
            <div>
              <div className="masterCert__contactLabel">REVISION NUMBER:</div>
              <InputText maxLength={30} value={revisionNumber} onChange={(e) => setRevisionNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
          </div>

          <div className="masterCert__disclaimer" style={{ marginBottom: 12 }}>
            This is to certify that the policies of insurance listed below have been issued to the insured named above for the policy period indicated. Notwithstanding any requirement, term or condition of any contract or other document with respect to which this certificate may be issued or may pertain, the insurance afforded by the policies described herein is subject to all the terms, exclusions and conditions of such policies. Limits shown may have been reduced by paid claims.
          </div>

          {/* Location of Premises / Description of Property */}
          <div className="masterCert__descriptionSection">
            <div className="masterCert__descriptionTitle">LOCATION OF PREMISES / DESCRIPTION OF PROPERTY (Attach ACORD 101, Additional Remarks Schedule, if more space is required)</div>
            <Dropdown
              value={selectedPropertyId}
              onChange={(e) => {
                setSelectedPropertyId(e.value);
                const selected = propertyOptions.find(p => p.value === e.value);
                if (selected) {
                  const newEntry = `Address: ${selected.label}`;
                  setLocationProperty(prev => {
                    const lines = prev ? prev.split('\n') : [];
                    return lines.includes(newEntry) ? prev : (prev ? `${prev}\n${newEntry}` : newEntry);
                  });
                  setDescriptionOfProperty(prev => {
                    const lines = prev ? prev.split('\n') : [];
                    return lines.includes(newEntry) ? prev : (prev ? `${prev}\n${newEntry}` : newEntry);
                  });
                }
                if (e.value && (isV2016 || showAcord101)) {
                  const formNumber = 2416;
                  fetchBuildingCoverages(e.value, formNumber)
                    .then(results => {
                      const allReturnObjects = results.flatMap((r: any) =>
                        Array.isArray(r.returnObjects) ? r.returnObjects : []
                      );
                      if (allReturnObjects.length > 0) prefillPropertyFromBuildingCoverages(allReturnObjects);
                    })
                    .catch((err) => console.error('building-coverages error:', err));
                }
              }}
              options={propertyOptions}
              optionLabel="label"
              optionValue="value"
              placeholder="Type and/or choose locations"
              className="masterCert__input masterCert__inputFull masterCert__inputMbSm"
              filter
              emptyMessage="No properties found"
            />
<InputTextarea value={descriptionOfProperty} onChange={(e) => setDescriptionOfProperty(e.target.value)} rows={3} className="masterCert__descriptionTextarea" />
          </div>

          {/* Coverage Table Header */}
          <div className="masterCert__coverageHeader24">
            <div>INSR<br/>LTR</div>
            <div>TYPE OF INSURANCE</div>
            <div>POLICY NUMBER</div>
            <div className="masterCert__coverageHeaderCenter">POLICY EFFECTIVE<br/>DATE (MM/DD/YY)</div>
            <div className="masterCert__coverageHeaderCenter">POLICY EXPIRATION<br/>DATE (MM/DD/YY)</div>
            <div>COVERED PROPERTY</div>
            <div>LIMITS</div>
          </div>

          {/* ── PROPERTY ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={propInsurerLtr} onChange={(e) => setPropInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="propChecked" checked={propChecked} onChange={(e) => setPropChecked(e.checked ?? false)} />
                <label htmlFor="propChecked" className="masterCert__coverageType" style={{ marginLeft: 4 }}>PROPERTY</label>
              </div>
              {/* Two-column: CAUSES OF LOSS | DEDUCTIBLES */}
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '3px 8px', marginTop: 4 }}>
                <div className="masterCert__contactLabel">CAUSES OF LOSS</div>
                <div className="masterCert__contactLabel">DEDUCTIBLES</div>

                {/* Row header: BUILDING deductible sub-label sits in deductibles col */}
                <div className="masterCert__checkboxItem"><Checkbox inputId="propBasic" checked={propBasic} onChange={(e) => setPropBasic(e.checked ?? false)} /><label htmlFor="propBasic" className="masterCert__checkboxItemLabel">BASIC</label></div>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#777', marginBottom: 1 }}>BUILDING</div>
                  <InputText value={propDeductBuilding} onChange={(e) => setPropDeductBuilding(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>

                <div className="masterCert__checkboxItem"><Checkbox inputId="propBroad" checked={propBroad} onChange={(e) => setPropBroad(e.checked ?? false)} /><label htmlFor="propBroad" className="masterCert__checkboxItemLabel">BROAD</label></div>
                <div />

                <div className="masterCert__checkboxItem"><Checkbox inputId="propSpecial" checked={propSpecial} onChange={(e) => setPropSpecial(e.checked ?? false)} /><label htmlFor="propSpecial" className="masterCert__checkboxItemLabel">SPECIAL</label></div>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#777', marginBottom: 1 }}>CONTENTS</div>
                  <InputText value={propDeductContents} onChange={(e) => setPropDeductContents(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>

                <div className="masterCert__checkboxItem"><Checkbox inputId="propEarthquake" checked={propEarthquake} onChange={(e) => setPropEarthquake(e.checked ?? false)} /><label htmlFor="propEarthquake" className="masterCert__checkboxItemLabel">EARTHQUAKE</label></div>
                <InputText value={propEarthquakeDeduct} onChange={(e) => setPropEarthquakeDeduct(e.target.value)} className="masterCert__input masterCert__inputFull" />

                <div className="masterCert__checkboxItem"><Checkbox inputId="propWind" checked={propWind} onChange={(e) => setPropWind(e.checked ?? false)} /><label htmlFor="propWind" className="masterCert__checkboxItemLabel">WIND</label></div>
                <InputText value={propWindDeduct} onChange={(e) => setPropWindDeduct(e.target.value)} className="masterCert__input masterCert__inputFull" />

                <div className="masterCert__checkboxItem"><Checkbox inputId="propFlood" checked={propFlood} onChange={(e) => setPropFlood(e.checked ?? false)} /><label htmlFor="propFlood" className="masterCert__checkboxItemLabel">FLOOD</label></div>
                <InputText value={propFloodDeduct} onChange={(e) => setPropFloodDeduct(e.target.value)} className="masterCert__input masterCert__inputFull" />

                <div className="masterCert__checkboxItem" style={{ alignItems: 'center' }}><Checkbox inputId="propOther1" checked={propOther1} onChange={(e) => setPropOther1(e.checked ?? false)} /><InputText value={propOther1Text} onChange={(e) => setPropOther1Text(e.target.value)} className="masterCert__input" style={{ marginLeft: 4, flex: 1 }} /></div>
                <InputText value={propOther1Deduct} onChange={(e) => setPropOther1Deduct(e.target.value)} className="masterCert__input masterCert__inputFull" />

                <div className="masterCert__checkboxItem" style={{ alignItems: 'center' }}><Checkbox inputId="propOther2" checked={propOther2} onChange={(e) => setPropOther2(e.checked ?? false)} /><InputText value={propOther2Text} onChange={(e) => setPropOther2Text(e.target.value)} className="masterCert__input" style={{ marginLeft: 4, flex: 1 }} /></div>
                <InputText value={propOther2Deduct} onChange={(e) => setPropOther2Deduct(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
            <InputText value={propPolicyNumber} onChange={(e) => setPropPolicyNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={propEffDate} onChange={(e) => setPropEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={propExpDate} onChange={(e) => setPropExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            {/* Covered Property + Limits (spans 2 columns) */}
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBuilding" checked={propCovBuilding} onChange={(e) => setPropCovBuilding(e.checked ?? false)} /><label htmlFor="propCovBuilding" className="masterCert__checkboxItemLabel">BUILDING</label></div>
              <InputText value={propLimBuilding} onChange={(e) => setPropLimBuilding(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovPP" checked={propCovPersonalProp} onChange={(e) => setPropCovPersonalProp(e.checked ?? false)} /><label htmlFor="propCovPP" className="masterCert__checkboxItemLabel">PERSONAL PROPERTY</label></div>
              <InputText value={propLimPersonalProp} onChange={(e) => setPropLimPersonalProp(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBI" checked={propCovBusinessIncome} onChange={(e) => setPropCovBusinessIncome(e.checked ?? false)} /><label htmlFor="propCovBI" className="masterCert__checkboxItemLabel">BUSINESS INCOME</label></div>
              <InputText value={propLimBusinessIncome} onChange={(e) => setPropLimBusinessIncome(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovEE" checked={propCovExtraExpense} onChange={(e) => setPropCovExtraExpense(e.checked ?? false)} /><label htmlFor="propCovEE" className="masterCert__checkboxItemLabel">EXTRA EXPENSE</label></div>
              <InputText value={propLimExtraExpense} onChange={(e) => setPropLimExtraExpense(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovRV" checked={propCovRentalValue} onChange={(e) => setPropCovRentalValue(e.checked ?? false)} /><label htmlFor="propCovRV" className="masterCert__checkboxItemLabel">RENTAL VALUE</label></div>
              <InputText value={propLimRentalValue} onChange={(e) => setPropLimRentalValue(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBB" checked={propCovBlanketBuilding} onChange={(e) => setPropCovBlanketBuilding(e.checked ?? false)} /><label htmlFor="propCovBB" className="masterCert__checkboxItemLabel">BLANKET BUILDING</label></div>
              <InputText value={propLimBlanketBuilding} onChange={(e) => setPropLimBlanketBuilding(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBPP" checked={propCovBlanketPersProp} onChange={(e) => setPropCovBlanketPersProp(e.checked ?? false)} /><label htmlFor="propCovBPP" className="masterCert__checkboxItemLabel">BLANKET PERS PROP</label></div>
              <InputText value={propLimBlanketPersProp} onChange={(e) => setPropLimBlanketPersProp(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propCovBBPP" checked={propCovBlanketBldgPP} onChange={(e) => setPropCovBlanketBldgPP(e.checked ?? false)} /><label htmlFor="propCovBBPP" className="masterCert__checkboxItemLabel">BLANKET BLDG &amp; PP</label></div>
              <InputText value={propLimBlanketBldgPP} onChange={(e) => setPropLimBlanketBldgPP(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propOtherCov1" checked={propOtherCov1} onChange={(e) => setPropOtherCov1(e.checked ?? false)} /><InputText value={propOtherCov1Desc} onChange={(e) => setPropOtherCov1Desc(e.target.value)} placeholder="Other" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={propOtherCov1Lim} onChange={(e) => setPropOtherCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="propOtherCov2" checked={propOtherCov2} onChange={(e) => setPropOtherCov2(e.checked ?? false)} /><InputText value={propOtherCov2Desc} onChange={(e) => setPropOtherCov2Desc(e.target.value)} placeholder="Other" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={propOtherCov2Lim} onChange={(e) => setPropOtherCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* ── INLAND MARINE ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={inlandInsurerLtr} onChange={(e) => setInlandInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="inlandChecked" checked={inlandChecked} onChange={(e) => setInlandChecked(e.checked ?? false)} />
                <label htmlFor="inlandChecked" className="masterCert__coverageType" style={{ marginLeft: 4 }}>INLAND MARINE</label>
              </div>
              <div className="masterCert__contactLabel" style={{ marginTop: 4 }}>CAUSES OF LOSS</div>
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandNamedPerils" checked={inlandNamedPerils} onChange={(e) => setInlandNamedPerils(e.checked ?? false)} /><label htmlFor="inlandNamedPerils" className="masterCert__checkboxItemLabel">NAMED PERILS</label></div>
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandOtherPerils" checked={inlandOtherPerils} onChange={(e) => setInlandOtherPerils(e.checked ?? false)} /><InputText value={inlandOtherPerilsText} onChange={(e) => setInlandOtherPerilsText(e.target.value)} placeholder="" className="masterCert__input masterCert__inputSm" /></div>
              <div style={{ fontSize: '0.75rem', color: '#333', marginTop: 6, marginBottom: 2 }}>TYPE OF POLICY</div>
              <InputText value={inlandTypeOfPolicy} onChange={(e) => setInlandTypeOfPolicy(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
            <InputText value={inlandPolicyNumber} onChange={(e) => setInlandPolicyNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={inlandEffDate} onChange={(e) => setInlandEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={inlandExpDate} onChange={(e) => setInlandExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandCov1" checked={inlandCov1} onChange={(e) => setInlandCov1(e.checked ?? false)} /><InputText value={inlandCov1Desc} onChange={(e) => setInlandCov1Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={inlandCov1Lim} onChange={(e) => setInlandCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandCov2" checked={inlandCov2} onChange={(e) => setInlandCov2(e.checked ?? false)} /><InputText value={inlandCov2Desc} onChange={(e) => setInlandCov2Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={inlandCov2Lim} onChange={(e) => setInlandCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandCov3" checked={inlandCov3} onChange={(e) => setInlandCov3(e.checked ?? false)} /><InputText value={inlandCov3Desc} onChange={(e) => setInlandCov3Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={inlandCov3Lim} onChange={(e) => setInlandCov3Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="inlandCov4" checked={inlandCov4} onChange={(e) => setInlandCov4(e.checked ?? false)} /><InputText value={inlandCov4Desc} onChange={(e) => setInlandCov4Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={inlandCov4Lim} onChange={(e) => setInlandCov4Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* ── CRIME ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={crimeInsurerLtr} onChange={(e) => setCrimeInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="crimeChecked" checked={crimeChecked} onChange={(e) => setCrimeChecked(e.checked ?? false)} />
                <label htmlFor="crimeChecked" className="masterCert__coverageType" style={{ marginLeft: 4 }}>CRIME</label>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#333', marginTop: 4, marginBottom: 2 }}>TYPE OF POLICY</div>
              <InputTextarea value={crimeTypeOfPolicy} onChange={(e) => setCrimeTypeOfPolicy(e.target.value)} rows={2} className="masterCert__input masterCert__inputFull" autoResize />
            </div>
            <InputText value={crimePolicyNumber} onChange={(e) => setCrimePolicyNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={crimeEffDate} onChange={(e) => setCrimeEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={crimeExpDate} onChange={(e) => setCrimeExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="crimeCov1" checked={crimeCov1} onChange={(e) => setCrimeCov1(e.checked ?? false)} /><InputText value={crimeCov1Desc} onChange={(e) => setCrimeCov1Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={crimeCov1Lim} onChange={(e) => setCrimeCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="crimeCov2" checked={crimeCov2} onChange={(e) => setCrimeCov2(e.checked ?? false)} /><InputText value={crimeCov2Desc} onChange={(e) => setCrimeCov2Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={crimeCov2Lim} onChange={(e) => setCrimeCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="crimeCov3" checked={crimeCov3} onChange={(e) => setCrimeCov3(e.checked ?? false)} /><InputText value={crimeCov3Desc} onChange={(e) => setCrimeCov3Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={crimeCov3Lim} onChange={(e) => setCrimeCov3Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* ── BOILER & MACHINERY / EQUIPMENT BREAKDOWN ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={boilerInsurerLtr} onChange={(e) => setBoilerInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="boilerChecked" checked={boilerChecked} onChange={(e) => setBoilerChecked(e.checked ?? false)} />
                <label htmlFor="boilerChecked" className="masterCert__coverageType" style={{ marginLeft: 4 }}>BOILER &amp; MACHINERY / EQUIPMENT BREAKDOWN</label>
              </div>
            </div>
            <InputText value={boilerPolicyNumber} onChange={(e) => setBoilerPolicyNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={boilerEffDate} onChange={(e) => setBoilerEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={boilerExpDate} onChange={(e) => setBoilerExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="boilerCov1" checked={boilerCov1} onChange={(e) => setBoilerCov1(e.checked ?? false)} /><InputText value={boilerCov1Desc} onChange={(e) => setBoilerCov1Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={boilerCov1Lim} onChange={(e) => setBoilerCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="boilerCov2" checked={boilerCov2} onChange={(e) => setBoilerCov2(e.checked ?? false)} /><InputText value={boilerCov2Desc} onChange={(e) => setBoilerCov2Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={boilerCov2Lim} onChange={(e) => setBoilerCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* ── OTHER ── */}
          <div className="masterCert__coverageRow24">
            <div className="masterCert__coverageInsr">
              <Dropdown value={otherInsurerLtr} onChange={(e) => setOtherInsurerLtr(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__coverageType">OTHER</div>
              <InputTextarea maxLength={100} value={otherType} onChange={(e) => setOtherType(e.target.value)} rows={2} className="masterCert__input masterCert__inputFull" autoResize />
            </div>
            <InputText maxLength={50} value={otherPolicyNumber} onChange={(e) => setOtherPolicyNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={otherEffDate} onChange={(e) => setOtherEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={otherExpDate} onChange={(e) => setOtherExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__coveredLimits">
              <div className="masterCert__checkboxItem"><Checkbox inputId="otherCov1" checked={otherCov1} onChange={(e) => setOtherCov1(e.checked ?? false)} /><InputText value={otherCov1Desc} onChange={(e) => setOtherCov1Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={otherCov1Lim} onChange={(e) => setOtherCov1Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__checkboxItem"><Checkbox inputId="otherCov2" checked={otherCov2} onChange={(e) => setOtherCov2(e.checked ?? false)} /><InputText value={otherCov2Desc} onChange={(e) => setOtherCov2Desc(e.target.value)} placeholder="Coverage" className="masterCert__input masterCert__checkboxItemLabel" /></div>
              <InputText value={otherCov2Lim} onChange={(e) => setOtherCov2Lim(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>
        </div>

        {/* SPECIAL CONDITIONS / OTHER COVERAGES */}
        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">SPECIAL CONDITIONS / OTHER COVERAGES (Attach ACORD 101, Additional Remarks Schedule, if more space is required)</div>
          <InputTextarea value={specialConditions} onChange={(e) => setSpecialConditions(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>

        {/* CERTIFICATE HOLDER & CANCELLATION */}
        <div className="masterCert__bottomRow">
          <div className="masterCert__certHolderBox" style={!holderEnabled ? { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', minHeight: 140 } : {}}>
            {!holderEnabled ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0, color: '#9ca3af' }}>CERTIFICATE HOLDER</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '11px', color: '#9ca3af' }}>
                  <i className="pi pi-info-circle" style={{ fontSize: '11px' }} />
                  <span>Certificate holder can be added while sending certificate.</span>
                </div>
                <Button
                  label="Enable"
                  icon="pi pi-lock-open"
                  size="small"
                  outlined
                  style={{ height: '28px', fontSize: '11px', padding: '0 14px', borderRadius: '4px', fontWeight: 500 }}
                  onClick={() => setHolderEnabled(true)}
                />
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>CERTIFICATE HOLDER</div>
                  <Button
                    label="Disable"
                    icon="pi pi-lock"
                    size="small"
                    style={{ height: '22px', fontSize: '10px', padding: '0 8px', borderRadius: '4px', fontWeight: 500 }}
                    onClick={() => { setHolderEnabled(false); setHolderName(''); setHolderAddress(''); setHolderCity(''); setHolderStreet(''); setHolderState(''); setHolderZip(''); setHolderSearchQuery(''); }}
                  />
                </div>
                <div style={{ position: 'relative', width: '100%', marginBottom: 6 }}>
                  <i className="pi pi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 13, zIndex: 1, pointerEvents: 'none' }} />
                  <AutoComplete
                    value={holderSearchQuery}
                    suggestions={holderSuggestions.map((h) => ({ label: h.name, value: h }))}
                    completeMethod={(e) => setHolderSearchQuery(e.query)}
                    onChange={(e) => { if (typeof e.value === 'string') setHolderSearchQuery(e.value); }}
                    field="label"
                    placeholder="Search for a certificate holder"
                    style={{ width: '100%' }}
                    inputStyle={{ width: '100%', paddingLeft: 30 }}
                    onSelect={(e) => {
                      const h = e.value.value;
                      setHolderName(h.name || '');
                      setHolderAddress(h.addressLine1 || '');
                      setHolderCity(h.city || '');
                      setHolderState(h.stateName || '');
                      setHolderZip(h.zipCode || '');
                      setHolderSearchQuery('');
                    }}
                  />
                </div>
                <InputText maxLength={100} value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
                <InputText maxLength={100} value={holderAddress} onChange={(e) => setHolderAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
                <InputText maxLength={100} value={holderCity} onChange={(e) => setHolderCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
                <div className="masterCert__addressGrid">
                  <InputText maxLength={100} value={holderStreet} onChange={(e) => setHolderStreet(e.target.value)} placeholder="Street" className="masterCert__input masterCert__inputFull" />
                  <InputText maxLength={50} value={holderState} onChange={(e) => setHolderState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
                  <InputText maxLength={20} value={holderZip} onChange={(e) => setHolderZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
                </div>
              </>
            )}
          </div>
          <div className="masterCert__cancellationBox">
            <div className="masterCert__sectionHeader">CANCELLATION</div>
            <div className="masterCert__cancellationText">
              Should any of the above described policies be cancelled before the expiration date thereof, notice will be delivered in accordance with the policy provisions.
            </div>
            <div className="masterCert__authRepLabel">AUTHORIZED REPRESENTATIVE</div>
            <div className="masterCert__authRepRow">
              <Dropdown value={authRepSignature} onChange={(e) => setAuthRepSignature(e.value)} options={signatureOptions} optionLabel="label" optionValue="value" placeholder="Select signature:" className="masterCert__input masterCert__authRepDropdown" emptyMessage="No available options" />
              <Button
                label="Create New"
                className="p-button-outlined carrierDialog__secondaryBtn"
                onClick={() => setShowAddSignature(true)}
              />
            </div>
              {signaturePreviewUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={signaturePreviewUrl} alt="Signature preview" style={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
                </div>
              )}
          </div>
        </div>

        {/* ACORD Footer */}
        <div className="masterCert__acordFooter">
          <div>{isV2016 ? 'ACORD 24 (2016/03)' : 'ACORD 24 (2009/09)'}</div>
          <div>&copy; {isV2016 ? '1995-2015' : '1995-2009'} ACORD CORPORATION. All rights reserved.</div>
        </div>

        {/* ── ACORD 101 - Additional Remarks Schedule ── */}
        {showAcord101 && (
          <div className="masterCert__acord101">
            <div className="masterCert__acordHeader" style={{ marginTop: 24 }}>
              <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
              <div className="masterCert__acordTitleWrapper">
                <div className="masterCert__acordTitle">ADDITIONAL REMARKS SCHEDULE</div>
              </div>
            </div>
            <div className="masterCert__twoColumnLayout">
              <div>
                <div className="masterCert__sectionHeader">AGENCY</div>
                <InputTextarea maxLength={250} value={acord101Agency} onChange={(e) => setAcord101Agency(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Agency name and address" />
              </div>
              <div>
                <div className="masterCert__sectionHeader">NAMED INSURED</div>
                <InputTextarea maxLength={100} value={acord101NamedInsured} onChange={(e) => setAcord101NamedInsured(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Named insured" />
              </div>
            </div>
            <div className="masterCert__descriptionSection">
              <div className="masterCert__descriptionTitle">ADDITIONAL REMARKS</div>
              <InputTextarea maxLength={2000} value={acord101AdditionalRemarks} onChange={(e) => setAcord101AdditionalRemarks(e.target.value)} rows={8} className="masterCert__descriptionTextarea" placeholder="Enter additional remarks..." />
            </div>
            <div className="masterCert__acordFooter">
              <div>ACORD 101 (2008/01)</div>
              <div>&copy; 2008 ACORD CORPORATION. All rights reserved.</div>
            </div>
          </div>
        )}
      </div>

      <CreateNewCarriers open={showCreateCarrier} onClose={() => setShowCreateCarrier(false)} />
      <AddNewSignature open={showAddSignature} onClose={() => setShowAddSignature(false)} onSaved={refetchSignatures} />
    </>
  );
});

export default Acord24Form;
